/**
 * Read side of useMemoRecords: parent/task association resolution and the
 * list query that feeds the records array. Split out of useMemoRecords.ts —
 * see that file for the composed public hook.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useAssociationMatchers } from '../../hooks/useAssociationMatchers';
import { crmQueryKeys } from '../../query/queries';
import { useCrmObjectList } from '../../store/hooks';
import type { MemoAssociationNames, MemoEntityType } from './notes.utils';
import { extractMemoData } from './notes.utils';

/**
 * Association types extractMemoData needs from the LIST query, for one
 * note-like family. Empty properties arrays: only target_entity_id is read.
 * Built once per family and memoized in the hook so the options object stays
 * referentially stable (see FRONTEND_PATTERNS.md § Hoist Constants).
 */
function listAssociationProjection(
  names: MemoAssociationNames
): Record<string, { properties: string[] }> {
  return {
    [names.owner]: { properties: [] },
    ...(names.stage ? { [names.stage]: { properties: [] } } : {}),
    [names.creator]: { properties: [] },
    [names.visibleOrg]: { properties: [] },
  };
}

export interface UseMemoRecordsQueriesParams {
  orgId: number;
  entityId?: string;
  entityTypeName?: string;
  recordType: MemoEntityType;
  names: MemoAssociationNames;
}

/**
 * Parent/task association resolution, the store-backed list query, and the
 * per-object invalidation helper the mutation hooks need after a write.
 */
export function useMemoRecordsQueries({
  orgId,
  entityId,
  entityTypeName,
  recordType,
  names,
}: UseMemoRecordsQueriesParams) {
  const queryClient = useQueryClient();

  // Invalidate the same key family that useCrmObjectList registers under, so
  // optimistic mutations refetch the actual list query (not a stale `['notes',
  // orgId, entityId]` key that nothing subscribes to). useMemo gives the array
  // a stable identity across renders so the mutation onSuccess callbacks
  // (recreated each render by react-query) don't capture a fresh array each
  // time and so it's safe to use in dep arrays.
  const notesListKey = useMemo(
    () => crmQueryKeys.objects(orgId, recordType),
    [orgId, recordType]
  );

  // Fetch association types + matchers (kind-aware) so we resolve associations
  // like ahoy_note_to_contact correctly. That one is kind-based on
  // entity_kind=person; the previous naive `target_entity_type_name === ...`
  // lookup returned null for contacts, which silently broke note creation and
  // the notes section gating.
  const { associationTypes, matchesSource, matchesTarget } =
    useAssociationMatchers(orgId);

  // The rule linking a record of `sourceType` to the parent entity type. Both
  // sides are kind-aware: a task reaches a contact through the case-kind
  // `ahoy_deal_to_contact` (source kind `case`), a note through the
  // person-kind `ahoy_note_to_contact`. A type-named rule wins over a
  // kind-based one when both exist.
  const parentLinkFor = useCallback(
    (sourceType: MemoEntityType): string | null => {
      if (entityTypeName === undefined) {
        return null;
      }
      const candidates = associationTypes.filter(
        at =>
          at.value_write_access !== 'internal' &&
          matchesSource(at, sourceType) &&
          matchesTarget(at, entityTypeName)
      );
      const preferred =
        candidates.find(at => at.source_entity_type_name === sourceType) ??
        candidates[0];
      return preferred?.association_type_name || null;
    },
    [associationTypes, matchesSource, matchesTarget, entityTypeName]
  );

  // Find the association type for this record family -> target entity type
  const associationTypeName = useMemo(
    () => parentLinkFor(recordType),
    [parentLinkFor, recordType]
  );

  // Task counterpart of the parent link (task -> target entity type), for a
  // staged creation on a migrated org — see createRecordMutation. Null when no
  // rule links a task to this parent type.
  const taskAssociationTypeName = useMemo(
    () => parentLinkFor('ahoy_task'),
    [parentLinkFor]
  );

  // Find the owner association type. Match by the family's canonical
  // `*_to_owner` name — there are multiple record→user association types
  // (owner, creator, visible_user, mentioned) and a (source, target) match
  // could pick the wrong one depending on list order.
  const ownerAssociationTypeName = useMemo(() => {
    const assocType = associationTypes.find(
      at => at.association_type_name === names.owner
    );
    return assocType?.association_type_name || null;
  }, [associationTypes, names.owner]);

  // Fetch notes via the store-aware list hook (handles pending creations,
  // optimistic deletion, and WS-driven property edits internally).
  // Gate fetching on entityId and ownerAssociationTypeName so the query does
  // not run before association metadata is ready (the previous useQuery
  // version had the same guard).
  const listEnabled = !!entityId && !!ownerAssociationTypeName;
  const listOptions = useMemo(
    () => ({
      enabled: listEnabled,
      // Associations are opt-in on the query endpoint. extractMemoData reads
      // owner/stage/creator/visible-to-org edges off each note (is_private is
      // DERIVED from the visible-to-org edge), so this projection is
      // load-bearing: without it every note renders as private with no
      // owner/stage/creator.
      include_associations: listAssociationProjection(names),
    }),
    [listEnabled, names]
  );
  const listResult = useCrmObjectList(
    orgId,
    recordType,
    {
      associated_with_entity_id: entityId,
      sort: 'ahoy_created_at',
      order: 'desc',
    },
    listOptions
  );

  // Map raw CRM objects through extractMemoData. names.stage is null for the
  // note family (no stage concept); extractMemoData treats a null stage
  // association name as "skip the stage lookup".
  const records = useMemo(
    () =>
      (listResult.data?.results ?? []).map(obj =>
        extractMemoData(obj, ownerAssociationTypeName, names.stage)
      ),
    [listResult.data?.results, ownerAssociationTypeName, names.stage]
  );

  // single-slideout.tsx fetches the focused object via its own query keyed
  // by ['crm', orgId, 'object', objectId] — separate from crmQueryKeys.objects
  // (which is the list query family). When the slideout's NoteRecordOverview
  // calls updateRecord/deleteRecord, we have to invalidate THIS key too so the
  // slideout's `note.last_event_seq` doesn't go stale ahead of the next WS
  // event, which would otherwise cause a sequence conflict on a rapid second
  // edit. The list-key invalidation above only refreshes the list cache.
  const invalidateNoteObjectQuery = useCallback(
    (noteEntityId: string) => {
      queryClient.invalidateQueries({
        queryKey: ['crm', orgId, 'object', noteEntityId],
      });
    },
    [queryClient, orgId]
  );

  return {
    records,
    isLoading: listResult.isLoading,
    error: listResult.error,
    refetch: listResult.refetch,
    associationTypeName,
    taskAssociationTypeName,
    ownerAssociationTypeName,
    notesListKey,
    invalidateNoteObjectQuery,
  };
}
